import PropTypes from "prop-types";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import Dropdown from "../../../../parts/dropdown";
import SvgIcon from "../../../../parts/SvgIcon";
import {
  applySendDelay,
  emailConfig,
  findSendDelay,
  issueCodesOf,
  toApiSteps,
  updateStepConfig,
} from "../../../../utils/automation-builder";
import EmailEditor from "./EmailEditor";
import { saveChanges } from "./save-changes";

const BUILDER_URL = "/coaching-portal/custom-workflows/automations";

// The waits "Send Timing" offers; a wait already in the flow with another amount is kept as it is
const NOW = "now";
const TIMING_CHOICES = [
  { amount: 1, unit: "hours" },
  { amount: 2, unit: "hours" },
  { amount: 1, unit: "days" },
];
const timingKey = ({ amount, unit }) => `${amount}:${unit}`;
const timingFromKey = (key) => {
  if (key === NOW) return null;
  const [amount, unit] = key.split(":");
  return { amount: Number(amount), unit };
};

// Who the email comes from: the account's own address once its domain is verified, MSB's otherwise
const FromField = ({ settings }) => {
  const { t } = useTranslation("coaching-portal");
  const tr = (key) => t(`emailAutomations.builder.emailPanel.${key}`);
  const verified = settings?.state === "verified" && settings.fromEmail;
  const name = settings?.fromName || "MyStrengthBook";
  return (
    <div className="ep-field">
      <span className="ep-label">{tr("from")}</span>
      <div className="ep-from">
        <span>{verified ? `${name} <${settings.fromEmail}>` : name}</span>
        <span className={`ep-badge${verified ? " ep-badge--ok" : ""}`}>
          {verified ? (
            <SvgIcon
              src="/img/new-icons/check.svg"
              useCurrentColor
              classNames="size-5 shrink-0"
              aria-hidden="true"
            />
          ) : null}
          {verified ? tr("verified") : tr("defaultSender")}
        </span>
      </div>
    </div>
  );
};

FromField.propTypes = { settings: PropTypes.object };

// What goes under the body of a marketing email: the business address, who sends it and the way to
// unsubscribe (as the API builds it, see sending/message.js)
const EmailFooter = ({ settings }) => {
  const { t } = useTranslation("coaching-portal");
  const name = settings?.fromName || "MyStrengthBook";
  return (
    <>
      <span>
        {settings?.postalAddress ||
          t("emailAutomations.builder.emailPanel.footerAddressMissing")}
        {` · Sent by ${name}`}
      </span>
      <span className="ee-footer__link">Unsubscribe</span>
    </>
  );
};

EmailFooter.propTypes = { settings: PropTypes.object };

// Editing one email step: what it is, what it says and when it goes out
const EmailPanel = ({
  automationId,
  steps,
  index,
  mergeFields,
  sendingSettings,
  issues,
  closeHref,
}) => {
  const { t } = useTranslation("coaching-portal");
  const tr = (key) => t(`emailAutomations.builder.emailPanel.${key}`);
  const saved = steps[index].config;
  const [marketing, setMarketing] = useState(Boolean(saved.marketing));
  const [subject, setSubject] = useState(saved.subject ?? "");
  const [preheader, setPreheader] = useState(saved.preheader ?? "");
  const [body, setBody] = useState(saved.body ?? "");
  const [asTemplate, setAsTemplate] = useState(false);
  const wait = findSendDelay(steps, index);
  const [timing, setTiming] = useState(wait ? timingKey(wait) : NOW);
  const timingChoices =
    wait &&
    !TIMING_CHOICES.some((choice) => timingKey(choice) === timingKey(wait))
      ? [...TIMING_CHOICES, wait]
      : TIMING_CHOICES;
  const timingOptions = [
    { text: tr("timingImmediately"), value: NOW },
    ...timingChoices.map((choice) => ({
      text: t(`emailAutomations.builder.emailPanel.timing_${choice.unit}`, {
        count: choice.amount,
      }),
      value: timingKey(choice),
    })),
  ];
  const codes = issueCodesOf(issues, index);
  const hasNext = index + 1 < steps.length;

  const save = (goNext) => {
    // A wait chosen in Send Timing is the Delay step right before this email
    const placed = applySendDelay(steps, index, timingFromKey(timing));
    const edited = emailConfig(saved, { subject, preheader, body, marketing });
    const nextSteps = updateStepConfig(placed.steps, placed.index, edited);
    const next =
      placed.index + 1 < nextSteps.length ? `?step=${placed.index + 1}` : "";
    // Once the email is saved, the Save as template modal opens over the screen that follows
    const modal = asTemplate
      ? `${next ? "&" : "?"}modal[type]=saveAsTemplate&automation=${automationId}`
      : "";
    saveChanges(
      automationId,
      { steps: toApiSteps(nextSteps) },
      goNext || asTemplate
        ? `${BUILDER_URL}/${automationId}${next}${modal}`
        : undefined
    );
  };

  return (
    <div className="ab-panel ep">
      <h3>{tr("title")}</h3>

      <div className="ep-kind" role="tablist">
        {[false, true].map((value) => (
          <button
            key={String(value)}
            type="button"
            role="tab"
            aria-selected={marketing === value}
            onClick={() => setMarketing(value)}
          >
            {tr(value ? "marketing" : "transactional")}
          </button>
        ))}
      </div>
      <p className="ep-hint">
        {tr(marketing ? "marketingHint" : "transactionalHint")}
      </p>

      <FromField settings={sendingSettings} />

      <div className="ep-field">
        <span className="ep-label">
          {tr("subject")}
          {codes.includes("email_without_subject") ? (
            <em>{tr("subjectMissing")}</em>
          ) : null}
        </span>
        <EmailEditor
          key={`subject-${index}`}
          value={subject}
          fields={mergeFields}
          placeholder={tr("subjectPlaceholder")}
          ariaLabel={tr("subject")}
          onChange={setSubject}
        />
      </div>

      <div className="ep-field">
        <span className="ep-label">{tr("preheader")}</span>
        <input
          type="text"
          aria-label={tr("preheader")}
          value={preheader}
          maxLength={150}
          placeholder={tr("preheaderPlaceholder")}
          onChange={(event) => setPreheader(event.target.value)}
        />
      </div>

      <div className="ep-field">
        <span className="ep-label">
          {tr("body")}
          {codes.includes("email_without_body") ? (
            <em>{tr("bodyMissing")}</em>
          ) : null}
        </span>
        <EmailEditor
          key={`body-${index}`}
          value={body}
          fields={mergeFields}
          multiline
          defaultOpen
          footer={marketing ? <EmailFooter settings={sendingSettings} /> : null}
          placeholder={tr("bodyPlaceholder")}
          ariaLabel={tr("body")}
          onChange={setBody}
        />
      </div>

      <div className="ep-timing">
        <span className="ep-label">{tr("timing")}</span>
        <div className="ep-select">
          <Dropdown
            overlay
            openUpward
            summaryText={
              timingOptions.find((option) => option.value === timing)?.text
            }
            selectedValue={timing}
            options={timingOptions}
            onSelectFn={setTiming}
          />
        </div>
      </div>

      <div className="ep-footer">
        <label className="ab-check" htmlFor={`ep-template-${index}`}>
          <input
            id={`ep-template-${index}`}
            type="checkbox"
            className="sr-only"
            checked={asTemplate}
            onChange={(event) => setAsTemplate(event.target.checked)}
          />
          <i aria-hidden="true" />
          <span>{tr("saveAsTemplate")}</span>
        </label>
        <div className="ab-panel__actions">
          {/* biome-ignore lint/a11y/useSemanticElements: a real link so Rill navigates; role only picks up button styles */}
          <a
            href={closeHref}
            className="ep-cancel"
            data-button-type="secondary"
            role="button"
          >
            {tr("cancel")}
          </a>
          <button
            type="button"
            data-button-type="primary"
            onClick={() => save(true)}
          >
            {tr(hasNext ? "saveNext" : "save")}
          </button>
        </div>
      </div>
    </div>
  );
};

EmailPanel.propTypes = {
  automationId: PropTypes.string.isRequired,
  steps: PropTypes.array.isRequired,
  index: PropTypes.number.isRequired,
  mergeFields: PropTypes.array.isRequired,
  sendingSettings: PropTypes.object,
  issues: PropTypes.array,
  closeHref: PropTypes.string.isRequired,
};

export default EmailPanel;
